import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { KeyRound, Copy, Check } from "lucide-react";

interface IssueAccessCodeButtonProps {
  customerId: string;
  customerName: string;
}

interface IssuedCode {
  code: string;
  expiresAt: string;
  customerPhone: string | null;
}

export function IssueAccessCodeButton({ customerId, customerName }: IssueAccessCodeButtonProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [issued, setIssued] = useState<IssuedCode | null>(null);
  const [copied, setCopied] = useState(false);

  // SUPER admins only
  if (user?.role !== "SUPER") return null;

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/admin/customers/${customerId}/access-code`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to issue access code");
      }
      return res.json() as Promise<IssuedCode>;
    },
    onSuccess: (data) => {
      setIssued(data);
      setCopied(false);
    },
    onError: (err: Error) => {
      toast({ title: "Failed", description: err.message, variant: "destructive" });
    },
  });

  const handleCopy = async () => {
    if (!issued) return;
    await navigator.clipboard.writeText(issued.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setIssued(null);
    setCopied(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-2"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
      >
        <KeyRound className="h-3.5 w-3.5" />
        {mutation.isPending ? "Issuing..." : "Issue Access Code"}
      </Button>

      <Dialog open={!!issued} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Access Code for {customerName}</DialogTitle>
            <DialogDescription>
              Share this code with the customer out-of-band (phone, in person). They enter it on the
              portal login screen along with their phone number. The code is single-use and expires
              in 15 minutes.
            </DialogDescription>
          </DialogHeader>
          {issued && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/40 p-6 text-center">
                <div className="font-mono text-4xl tracking-[0.5em] font-bold">{issued.code}</div>
              </div>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Phone: {issued.customerPhone || "—"}</span>
                <span>Expires: {new Date(issued.expiresAt).toLocaleTimeString()}</span>
              </div>
              <Button onClick={handleCopy} className="w-full gap-2">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy Code"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
